import { CLEAR_DAY, DAILY_THEMES, FUEL, effectsOf, type ConditionEffects, type Conditions, type DailyTheme, type PowerUpId, type GiftKind, type StarFactor } from '@accra/shared';
import type { TrafficKind } from '../content/traffic';

export interface BusState {
  /** Distance along the road, metres (keeps growing across laps). */
  s: number;
  x: number;
  lane: number;
  targetLane: number;
  y: number;
  vy: number;
  speed: number;
  /** Current cruise speed; ramps up over the run. */
  cruise: number;
  /** Seconds left in which a second stumble becomes a crash. */
  stumbleWindow: number;
  capacity: number;
  size: VehicleSize;
  /** 0–100; the trotro breaks down at 100 and the shift ends. */
  damage: number;
  /** Seconds left sitting still after a crash. */
  recover: number;
  /** Seconds left in which contacts are ignored (after a crash or scrape). */
  invulnerable: number;
  /** Sideways speed (m/s), heading (radians) and sideways acceleration, from the steering model. */
  vx: number;
  yaw: number;
  lateralAccel: number;
  /** Touch screens: lane changes are a quick flick instead of a smooth glide, and speed builds faster. */
  touchDriving: boolean;
  /** Tyre grip and pulling-away strength from the weather (1 = normal). */
  grip: number;
  accelFactor: number;
  /** Slope of the road under us in our direction: + climbs, − descends (0.05 is a steep hill). */
  grade: number;
  /** Out of fuel: the engine is dead, the trotro coasts to a stop. */
  engineOff: boolean;
  /** Speed multiplier (Siren Boost). */
  speedBoost: number;
  /** Hop height multiplier (Speed-Ramp Launch). */
  hopBoost: number;
}

export interface VehicleSize {
  length: number;
  width: number;
  height: number;
}

export interface TrafficCar {
  id: number;
  kind: TrafficKind;
  s: number;
  lane: number;
  /** Lateral position and speed: cars steer into their lane like the trotro does. */
  x: number;
  vx: number;
  speed: number;
  /** Which cached look to use for this car. */
  variant: number;
  closeCallCounted: boolean;
  /** Seconds left of speeding up after being hooted at with nowhere to pull over. */
  hurry?: number;
  /** After a crash: seconds standing still, seconds it can't be hit again, and how far it's swung round. */
  stunned?: number;
  noContact?: number;
  spin?: number;
}

export interface Passenger {
  /** Stop sequence number this passenger wants to get down at. */
  destination: number;
  boardedAtS: number;
}

/** Someone walking between the stop and the trotro door. */
export interface Walker {
  id: number;
  kind: 'board' | 'alight';
  /** Seconds since they started walking. */
  t: number;
  /** Boarders: which crowd spot they left from. */
  fromSlot: number;
  /** Alighters: where along the road they stepped off. */
  s: number;
}

/** Something to pick up on the road: a pesewa coin, a power-up crate, a score star or a gift. */
export interface Pickup {
  id: number;
  kind: 'coin' | 'crate' | 'star' | 'gift';
  power: PowerUpId | null;
  /** Score stars: ×2, ×3 or ×4. */
  factor?: StarFactor;
  gift?: GiftKind;
  s: number;
  /** Lateral position (coins drift towards the trotro under the magnet). */
  x: number;
  y: number;
}

/** Upgrade level (0–5) per power-up, from the garage. */
export type UpgradeLevels = Record<PowerUpId, number>;

/** A car on the crossing road at a junction. dir +1 drives towards +x (left to right). */
export interface CrossCar {
  id: number;
  kind: TrafficKind;
  junctionSeq: number;
  dir: 1 | -1;
  /** Offset from the junction centre along our road. */
  laneOffset: number;
  x: number;
  speed: number;
  variant: number;
  stunned?: number;
  noContact?: number;
  spin?: number;
}

/** Someone on the zebra crossing. dir -1 walks from the kerb to the median. */
export interface ZebraWalker {
  id: number;
  junctionSeq: number;
  x: number;
  dir: 1 | -1;
  speed: number;
  seed: number;
}

export interface JunctionState {
  seq: number;
  announced: boolean;
  /** Set once we've crossed this junction's stop line. */
  crossedStopLine: boolean;
  spawnTimer: number;
  /** People already sent across during this red. */
  zebraSent: number;
  zebraTimer: number;
  /** Fined for not stopping for pedestrians at this junction already. */
  pedestrianFined: boolean;
}

export interface StopVisit {
  /** Sequence number: lap * stops-per-lap + index. */
  seq: number;
  waiting: number;
  stopped: boolean;
  rated: boolean;
  /** Time until the next person steps on or off. */
  stepTimer: number;
  approachAnnounced: boolean;
  /** Passengers still to step off here, in order. */
  alightQueue: Passenger[];
}

export interface RunStats {
  score: number;
  metres: number;
  faresPesewas: number;
  passengersCarried: number;
  passengersDropped: number;
  closeCalls: number;
  perfectStops: number;
  stopsMissed: number;
  redLightsRun: number;
  /** Junctions crossed on green or amber. */
  cleanJunctions: number;
  horns: number;
  coins: number;
  powerUps: number;
  pedestrianOffences: number;
  rearEnds: number;
  scrapes: number;
  crashes: number;
  finesPesewas: number;
  repairsPesewas: number;
  /** Crashes by what was hit ('scrape' for scrapes), so the server can price the repairs itself. */
  repairsByKind: Record<string, number>;
  /** Fuel units bought and what they cost (pesewas). */
  fuelUnits: number;
  fuelPesewas: number;
  /** Police checkpoints driven through without stopping, and times the papers were a problem. */
  checkpointsRun: number;
  papersPaid: number;
  /** GH₵10 and GH₵20 dashes given to the police at checkpoints. */
  dashTen: number;
  dashTwenty: number;
  seconds: number;
}

/** The tank. Starts full; petrol stations sell more. */
export interface FuelState {
  units: number;
  /** What was in the tank when the shift began. */
  start: number;
  /** Units added at pumps this shift (fractions included); stats.fuelUnits is this rounded up. */
  bought: number;
  /** True while the pump is running. */
  filling: boolean;
  /** 0 none, 1 low, 2 very low: so each warning is given once. */
  warned: number;
  /** Seconds stood still with a dead engine. */
  stalledFor: number;
}

/** The officer wants a dash: none yet, asking now, you paid, or you refused. */
export type DashState = 'none' | 'asking' | 'paid' | 'refused';

/** The U-turn at the end of a trip (see systems/uturn). */
export interface TurnState {
  active: boolean;
  /** 0 → π round the bend. */
  theta: number;
  /** Where the turn began, and the lateral positions it runs between. */
  s0: number;
  x0: number;
  xEnd: number;
  /** World distance where the trip ends. */
  end: number;
  /** Which way the nose points, radians (0 = along the road, π = back the way we came). */
  heading: number;
  /** How many turns have been completed: the view re-centres the camera when it changes. */
  swaps: number;
}

/** A car on the other carriageway driving towards us. Scenery: it can't be hit. */
export interface OncomingCar {
  id: number;
  kind: TrafficKind;
  variant: number;
  lane: number;
  s: number;
  speed: number;
  /** The speed it would like to drive at. */
  cruise: number;
}

export type CheckpointPhase = 'approach' | 'checking' | 'cleared' | 'ran';

/** A police checkpoint on the road ahead. */
export interface Checkpoint {
  id: number;
  /** Where the officer stands (distance along the road). */
  s: number;
  phase: CheckpointPhase;
  /** Seconds spent being checked. */
  checkedFor: number;
  /** After checking: true if there was a problem with the papers. */
  papersProblem: boolean;
  /** The "checkpoint ahead" warning has been given. */
  announced: boolean;
  /** This officer will ask for a dash once you stop. */
  asksDash: boolean;
  dash: DashState;
  /** Seconds the question has been hanging. */
  askedFor: number;
}

export interface CheckpointState {
  current: Checkpoint | null;
  /** Where the next checkpoint will be set up. */
  nextAt: number;
  lastId: number;
}

/** Sprinter-sized by default. */
const DEFAULT_SIZE: VehicleSize = { length: 5.9, width: 2.0, height: 2.75 };

export interface RunState {
  bus: BusState;
  traffic: TrafficCar[];
  passengers: Passenger[];
  stop: StopVisit;
  /** People walking to the door, or away from it after getting down. */
  walkers: Walker[];
  crossTraffic: CrossCar[];
  zebra: ZebraWalker[];
  pickups: Pickup[];
  /** Seconds left on each active power-up (0 = off). */
  powers: Record<PowerUpId, number>;
  /** A score star's boost: the factor and seconds left (0 = none). */
  star: { factor: number; left: number };
  upgrades: UpgradeLevels;
  junction: JunctionState;
  stats: RunStats;
  legIndex: number;
  /** 0-based trip; -1 before the first. */
  trip: number;
  /** Today's theme (Double Fare Thursday…): fares, crowds, traffic. */
  theme: DailyTheme;
  /** Weather and time of day, and what they do (from the shift's seed). */
  conditions: Conditions;
  effects: ConditionEffects;
  fuel: FuelState;
  checkpoint: CheckpointState;
  turn: TurnState;
  /** A one-way journey reached its end. */
  arrived: boolean;
  oncoming: OncomingCar[];
  over: boolean;
  /** Why the shift ended. */
  endReason: 'breakdown' | 'endShift' | null;
  /** What caused the last crash (for the summary). */
  crashCause: string | null;
}

export function createRunState(capacity: number, cruiseStart: number, size: VehicleSize = DEFAULT_SIZE): RunState {
  return {
    bus: {
      s: 0, x: 0, lane: 1, targetLane: 1, y: 0, vy: 0,
      speed: 0, cruise: cruiseStart, stumbleWindow: 0, capacity, size, damage: 0, recover: 0, invulnerable: 0, engineOff: false, grip: 1, accelFactor: 1, grade: 0, speedBoost: 1, hopBoost: 1, vx: 0, yaw: 0, lateralAccel: 0, touchDriving: false,
    },
    traffic: [],
    passengers: [],
    stop: { seq: 0, waiting: 0, stopped: false, rated: false, stepTimer: 0, approachAnnounced: false, alightQueue: [] },
    walkers: [],
    crossTraffic: [],
    pickups: [],
    powers: { shield: 0, magnet: 0, double: 0, siren: 0, launch: 0 },
    star: { factor: 1, left: 0 },
    upgrades: { shield: 0, magnet: 0, double: 0, siren: 0, launch: 0 },
    junction: { seq: 0, announced: false, crossedStopLine: false, spawnTimer: 0, zebraSent: 0, zebraTimer: 0, pedestrianFined: false },
    zebra: [],
    stats: {
      score: 0, metres: 0, faresPesewas: 0, passengersCarried: 0, passengersDropped: 0,
      closeCalls: 0, perfectStops: 0, stopsMissed: 0, redLightsRun: 0, cleanJunctions: 0, horns: 0, coins: 0, powerUps: 0, pedestrianOffences: 0, rearEnds: 0, scrapes: 0, crashes: 0, finesPesewas: 0, repairsPesewas: 0, repairsByKind: {}, fuelUnits: 0, fuelPesewas: 0, checkpointsRun: 0, papersPaid: 0, dashTen: 0, dashTwenty: 0, seconds: 0,
    },
    legIndex: -1,
    trip: -1,
    theme: DAILY_THEMES[0]!,
    conditions: CLEAR_DAY,
    effects: effectsOf(CLEAR_DAY),
    fuel: { units: FUEL.tankUnits, start: FUEL.tankUnits, bought: 0, filling: false, warned: 0, stalledFor: 0 },
    checkpoint: { current: null, nextAt: 800, lastId: 0 },
    turn: { active: false, theta: 0, s0: 0, x0: 0, xEnd: 0, end: 0, heading: 0, swaps: 0 },
    oncoming: [],
    arrived: false,
    over: false,
    endReason: null,
    crashCause: null,
  };
}
